import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router-dom';
import SSTable from '../SSTable/SSTable';
import SSColumn from '../SSTable/SSColumn';
import UserProfile from '../UserProfile/UserProfile';
import ToolTipIcon from '../ToolTipIcon/ToolTipIcon';
import { isoDateToString } from '../../utils/formatUnits';
import { buildUrlForAddUniqueQueryParam } from '../../utils/updateQueryString';
import qs from '../../utils/qs';
import { checkPathAccess } from '../../utils/checkPathAccess';
import ToolTip from '../ToolTipIcon/ToolTip';

export class LocationsTable extends Component {
  getIcon = location => {
    const { userLocation } = this.props;
    const isAccessAllowed = checkPathAccess(userLocation, location);

    return isAccessAllowed ? (
      <img
        className="h-[30px] w-[30px] flex-shrink-0 max-w-none"
        src="/assets/images/icons/globe_no_bg.svg"
        alt="location icon"
      />
    ) : (
      <i className="material-icons restricted">vpn_locked</i>
    );
  };

  getName = location => (
    <div className="flex flex-col gap-1">
      <span data-test="location-name" className="poppins-regular font-500 text-dark-night">
        {location.name}
      </span>
      {location.parent && (
        <span data-test="location-parent" className="poppins-regular text-dark-night text-xs">
          {`Location: ${location.parent}`}
        </span>
      )}
    </div>
  );

  getDescription = location => {
    const { description } = location;
    let { tags = [] } = location || {};

    if (!tags || !Array.isArray(tags)) {
      tags = [];
    }

    return (
      <div data-test="location-description-container" className="flex flex-col gap-1">
        {description && <div className="poppins-regular text-dark-night">{description}</div>}
        {tags.length > 0 && (
          <div data-test="location-tags" className="flex flex-wrap gap-1">
            {tags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center rounded-md bg-orange-50 px-2 py-1 text-xs font-medium text-sunrise inset-ring inset-ring-orange-600/20"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  generateName = user => {
    if (!user) {
      return 'N/A';
    }

    const { firstname, lastname, name } = user;

    if (firstname.trim() === '' || lastname.trim() === '') {
      return name;
    }

    return `${firstname} ${lastname}`;
  };

  getUserFromUsers = userId => {
    const { users } = this.props;

    if (!userId) {
      return false;
    }

    const userPosition = users.findIndex(user => user.user_id === userId);

    if (userPosition === -1) {
      return false;
    }

    return users[userPosition];
  };

  getCreator = location => {
    const { users } = this.props;
    const user = this.getUserFromUsers(location.createdBy);

    return (
      <ToolTip
        toolText={`Created by ${this.generateName(user)} on ${isoDateToString(location.createdDate)}`}
        className="created-badge poppins-regular font-500 text-dark-night"
      >
        <UserProfile
          className="flex justify-start items-center"
          userId={location.createdBy}
          users={users}
          avatarHolderClasses="flex items-center justify-center"
        />
      </ToolTip>
    );
  };

  getModifier = location => {
    const { users } = this.props;
    const user = this.getUserFromUsers(location.createdBy);

    return (
      <ToolTip
        toolText={`Created by ${this.generateName(user)} on ${isoDateToString(location.modifiedDate)}`}
        className="modified-badge poppins-regular font-500 text-dark-night overflow-hidden max-w-[40px]"
      >
        <UserProfile
          avatarHolderClasses="flex items-center justify-center"
          className="flex justify-start items-center"
          userId={location.modifiedBy}
          users={users}
        />
      </ToolTip>
    );
  };

  getUrl = item => {
    const { location } = this.props;
    const sub = (qs(this.props).parent || '').endsWith('*') ? '*' : '';
    const parent = `${item.parent + item.name}/${sub}`;

    return buildUrlForAddUniqueQueryParam(location, 'parent', parent);
  };

  onDoubleClick = (event, item) => {
    const { history } = this.props;

    history.push(this.getUrl(item));
  };

  editButton = location => {
    const { userLocation } = this.props;
    const isAccessAllowed = checkPathAccess(userLocation, location);

    if (isAccessAllowed) {
      return (
        <div className="w-full bg-transparent h-full flex justify-center items-center">
          <ToolTipIcon toolText="Edit Location">
            <Link to={`/locations/${location.id}`}>
              <img src="/assets/images/icons/edit.svg" alt="Edit" />
            </Link>
          </ToolTipIcon>
        </div>
      );
    }

    return (
      <ToolTipIcon className="restricted" toolText={`Access Restricted to ${location.name}`}>
        block
      </ToolTipIcon>
    );
  };

  nextIcon = location => (
    <Link
      data-test="location-view-button"
      to={this.getUrl(location)}
      className="!rounded-[15px] w-[59px] px-4 p-2 hover:!text-white !appearance-none !no-underline !cursor-pointer !h-[26px] !bg-btn-primary-form !text-white poppins-bold !font-[500]"
    >
      View
    </Link>
  );

  isDisabled = () => {
    const { userLocation } = this.props;

    if (userLocation) {
      return item => !checkPathAccess(userLocation, item);
    }

    return () => false;
  };

  render() {
    const { data, selected, onClick } = this.props;

    if (!data) {
      return null;
    }

    return (
      <SSTable
        className="responsive table-locations"
        data={data}
        rowHeight={90}
        rowsCount={data.length - 1}
        onSelect={onClick}
        doubleClick={this.onDoubleClick}
        selected={selected}
        selectable
        zebra
        isDisabled={this.isDisabled()}
      >
        <SSColumn width={1} cell={this.getIcon} header="" />
        <SSColumn width={7} cell={this.getName} header="Name" />
        <SSColumn width={7} cell={this.getDescription} header="Description" />
        <SSColumn width={6.5} cell={this.getCreator} header="Created" />
        <SSColumn width={6.5} cell={this.getModifier} header="Modified" />
        <SSColumn center width={2} cell={this.editButton} header="Edit" />
        <SSColumn width={2} cell={this.nextIcon} header="" />
      </SSTable>
    );
  }
}

LocationsTable.propTypes = {
  data: PropTypes.array.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
  selected: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  userLocation: PropTypes.string.isRequired,
};

export default withRouter(LocationsTable);
